/* ---- <featured-collection-tabs> ---- */
(() => {
  'use strict';
  if (customElements.get('featured-collection-tabs')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE = [0.16, 1, 0.3, 1];
  const OUT = 0.2;
  const SHIFT = 48;
  const DESIGN = !!window.Shopify?.designMode;

  // Theme editor: the tab picked last in each section, so a section re-rendered after a setting change opens on the
  // tab being edited instead of jumping back to the first one. Listened for from the start, before any tabs connect.
  const picked = new Map();
  if (DESIGN) {
    document.addEventListener('shopify:block:select', (e) => {
      const panel = e.target?.closest?.('[data-fcp-panel]');
      const section = panel?.closest('[id^="shopify-section-"]');
      if (section) picked.set(section.id, panel.id);
    });
  }

  class FeaturedCollectionTabs extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      // Set-up (measuring, observers, carousels) waits until the section nears the viewport (theme.js), so it
      // stays off the main thread while the first screen loads.
      const near = window.theme?.whenNear;
      if (near) near(this, () => { if (this.isConnected && !this.started) this.setup(); });
      else this.setup();
    }

    setup() {
      this.started = true;

      this.list = this.querySelector('.fcp__list');
      this.indicator = this.querySelector('.fcp__indicator');
      this.box = this.querySelector('.fcp__panels');
      if (!this.list || !this.box) { this.classList.add('is-ready'); return; }
      this.sectionKey = this.closest('[id^="shopify-section-"]')?.id;

      // One listener each for every tab, present or re-rendered later.
      this.list.addEventListener('click', (e) => {
        const i = this.tabs?.indexOf(e.target.closest('[data-fcp-tab]'));
        if (i >= 0) this.select(i);
      });
      this.list.addEventListener('focusin', (e) => {
        if (!e.target.matches('[data-fcp-tab]')) return;
        this.expand(this.panel(e.target));
        this.reveal(e.target);
      });
      // A pill under the mouse gets its products ready before the click (see expand()).
      this.list.addEventListener('pointerover', (e) => {
        const tab = e.target.closest('[data-fcp-tab]');
        if (tab) this.expand(this.panel(tab));
      }, { passive: true });
      this.list.addEventListener('keydown', (e) => this.onKey(e));
      this.list.addEventListener('scroll', () => this.updateFades(), { passive: true });

      if (!this.sync()) { this.classList.add('is-ready'); return; }

      this.classList.add('is-instant', 'is-ready');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-instant')));
      this.resizer = new ResizeObserver(() => {
        this.classList.add('is-instant');
        this.moveIndicator();
        this.updateFades();
        requestAnimationFrame(() => this.classList.remove('is-instant'));
      });
      this.resizer.observe(this.list);
      this.boxResizer = new ResizeObserver(() => this.placeArrows());
      this.boxResizer.observe(this.box);
      document.fonts?.ready?.then(() => { this.moveIndicator(); this.updateFades(); });
      this.list.addEventListener('animationend', (e) => {
        if (e.target === this.tabs[this.tabs.length - 1]) { this.moveIndicator(); this.updateFades(); }
      });

      if (DESIGN) {
        this.onBlockSelect = (e) => {
          const panel = e.target?.closest?.('[data-fcp-panel]');
          if (!panel || !this.contains(panel)) return;
          const i = this.tabs.findIndex((t) => t.getAttribute('aria-controls') === panel.id);
          if (i >= 0) this.select(i, { instant: true });
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        // The editor can re-render one tab block on its own: its new pill (with the new title) lands among the
        // panels, so move it into the tab row in place of the old one and keep the open tab open.
        this.panelWatch = new MutationObserver(() => {
          if (!this.box.querySelector(':scope > [data-fcp-tab]') && this.tabs.every((t) => this.panel(t))) return;
          this.sync();
          this.moveIndicator();
          this.updateFades();
        });
        this.panelWatch.observe(this.box, { childList: true });
      }
    }

    disconnectedCallback() {
      this.started = false;
      this.resizer?.disconnect();
      this.boxResizer?.disconnect();
      this.panelWatch?.disconnect();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.cancel();
      this.bound = false;
    }

    // Pills into the tab row (a re-rendered pill replaces the one with its id), tabs without a panel dropped, and
    // exactly one panel shown: the one already open, else the tab picked in the editor, else the first.
    sync() {
      this.box.querySelectorAll(':scope > [data-fcp-tab]').forEach((pill) => {
        const old = this.list.querySelector(`[data-fcp-tab][id="${CSS.escape(pill.id)}"]`);
        if (old) old.replaceWith(pill); else this.list.appendChild(pill);
      });
      const all = [...this.list.querySelectorAll('[data-fcp-tab]')];
      all.forEach((tab) => { tab.hidden = !this.panel(tab); });
      this.tabs = all.filter((tab) => !tab.hidden);
      if (!this.tabs.length) return false;

      const first = this.index === undefined;
      const want = this.openId || (DESIGN && picked.get(this.sectionKey));
      let index = want ? this.tabs.findIndex((t) => t.getAttribute('aria-controls') === want) : -1;
      if (index < 0) index = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
      this.index = index;
      this.openId = this.tabs[index].getAttribute('aria-controls');
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.style.setProperty('--i', i);
        this.panel(tab).hidden = !on;
      });
      this.indicator?.style.setProperty('--i', index);
      const shown = this.panel(this.tabs[index]);
      this.expand(shown);
      if (shown && !first) { this.settleReveals(shown); this.refreshCarousels(shown); }
      return true;
    }

    panel(tab) { return tab && document.getElementById(tab.getAttribute('aria-controls')); }

    // Tabs after the first arrive with their products in a <template> (blocks/_collection-tab.liquid), so the hidden
    // cards cost no style, layout or script work on load. Moving the content in makes them a normal panel.
    expand(panel) {
      const tpl = panel?.querySelector(':scope > template[data-fcp-defer]');
      if (tpl) tpl.replaceWith(tpl.content);
    }

    moveIndicator() {
      const tab = this.tabs[this.index];
      if (!tab || !this.indicator) return;
      const left = tab.offsetLeft, width = tab.offsetWidth, room = this.list.clientWidth;
      this.indicator.style.left = `${left}px`;
      this.indicator.style.right = `${room - left - width}px`;
    }

    placeArrows() {
      this.querySelectorAll('.fcp__panel:not([hidden]) ft-carousel').forEach((carousel) => {
        if (!carousel.querySelector('.fcp__side-arrows')) return;
        const media = carousel.querySelector('.ftc__track .media');
        if (!media || !media.offsetHeight) return;
        let top = media.offsetHeight / 2;
        for (let el = media; el && el !== carousel; el = el.offsetParent) top += el.offsetTop;
        carousel.style.setProperty('--fcp-arrow-top', `${Math.round(top)}px`);
      });
    }

    updateFades() {
      const { scrollLeft, clientWidth } = this.list;
      const last = this.tabs[this.tabs.length - 1];
      const end = last ? last.offsetLeft + last.offsetWidth : 0;
      this.list.classList.toggle('can-scroll-start', scrollLeft > 4);
      this.list.classList.toggle('can-scroll-end', scrollLeft + clientWidth < end - 4);
      // offsetLeft ignores transforms, so pills sliding in on entrance never count as overflow
      this.list.classList.toggle('is-overflowing', end > clientWidth + 1);
    }

    reveal(tab) {
      if (this.list.scrollWidth <= this.list.clientWidth) return;
      const left = tab.offsetLeft - (this.list.clientWidth - tab.offsetWidth) / 2;
      this.list.scrollTo({ left, behavior: REDUCED ? 'auto' : 'smooth' });
    }

    onKey(e) {
      const last = this.tabs.length - 1;
      let i = null;
      if (e.key === 'ArrowRight') i = this.index >= last ? 0 : this.index + 1;
      else if (e.key === 'ArrowLeft') i = this.index <= 0 ? last : this.index - 1;
      else if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = last;
      if (i === null) return;
      e.preventDefault();
      this.select(i, { focus: true });
    }

    settleReveals(panel) {
      panel.querySelectorAll('ft-reveal:not([played])').forEach((el) => {
        el.setAttribute('played', '');
        el.style.opacity = '';
        el.style.transform = '';
        el.querySelectorAll('.media > img, .media > video').forEach((m) => { m.style.transform = ''; });
      });
    }

    refreshCarousels(panel) {
      panel.querySelectorAll('ft-carousel').forEach((c) => {
        c.ensure?.(); // carousels set up lazily (theme.js): finish now that the panel is shown
        if (!c.track || typeof c.go !== 'function') return;
        c.applyPerView?.();
        c.go(c.index || 0, { instant: true });
      });
    }

    items(panel) {
      const vw = window.innerWidth;
      return [...panel.querySelectorAll('.ftc__track > *:not(.ftc__slide-clone), .product-grid > *')].filter((el) => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.right > 0 && r.left < vw;
      });
    }

    select(i, { focus = false, instant = false } = {}) {
      if (i === this.index || !this.tabs[i]) return;
      const dir = i > this.index ? 1 : -1;
      const toTab = this.tabs[i];
      const to = this.panel(toTab);
      this.expand(to);
      this.index = i;
      this.openId = to?.id;

      this.tabs.forEach((tab, n) => {
        tab.setAttribute('aria-selected', String(n === i));
      });
      if (focus) toTab.focus({ preventScroll: true });
      this.list.classList.toggle('is-forward', dir > 0);
      this.list.classList.toggle('is-backward', dir < 0);
      this.moveIndicator();
      this.reveal(toTab);

      this.cancel();
      const panels = this.tabs.map((tab) => this.panel(tab));
      const showing = panels.filter((p) => !p.hidden && p !== to);
      const M = window.Motion;

      if (REDUCED || instant || !M || !showing.length) {
        panels.forEach((p) => { p.hidden = p !== to; });
        this.settleReveals(to);
        this.refreshCarousels(to);
        this.placeArrows();
        this.settled();
        return;
      }

      const token = this.run;
      const [from, ...rest] = showing;
      rest.forEach((p) => { p.hidden = true; });
      const startHeight = this.box.offsetHeight;
      this.box.style.height = `${startHeight}px`;
      this.box.classList.add('is-switching');

      const out = M.animate(from, { opacity: [1, 0], transform: ['translateX(0)', `translateX(${-dir * SHIFT / 2}px)`] }, { duration: OUT, easing: 'ease-in' });
      this.anims.push(out);

      out.finished.then(() => {
        if (token !== this.run) return;
        from.hidden = true;
        from.style.opacity = '';
        from.style.transform = '';
        to.style.opacity = '';
        to.style.transform = '';
        to.hidden = false;
        this.settleReveals(to);
        this.refreshCarousels(to);
        this.placeArrows();

        const cards = this.items(to);
        if (cards.length) {
          const tracks = to.querySelectorAll('.ftc__track');
          tracks.forEach((t) => t.classList.add('is-entering'));
          cards.forEach((el) => { el.style.opacity = '0'; el.style.transform = `translateX(${dir * SHIFT}px)`; });
          const card = M.animate(cards, { opacity: 1, transform: 'translateX(0)' }, { duration: 0.75, easing: EASE, delay: M.stagger(0.06) });
          this.anims.push(card);
          card.finished.then(() => {
            if (token !== this.run) return;
            cards.forEach((el) => { el.style.opacity = ''; el.style.transform = ''; });
            tracks.forEach((t) => t.classList.remove('is-entering'));
          }).catch(() => {});
        }

        const endHeight = to.offsetHeight;
        const grow = M.animate(this.box, { height: [`${startHeight}px`, `${endHeight}px`] }, { duration: 0.5, easing: EASE });
        this.anims.push(grow);
        grow.finished.then(() => {
          if (token !== this.run) return;
          this.box.style.height = '';
          this.box.classList.remove('is-switching');
          this.settled();
        }).catch(() => {});
      }).catch(() => {});
    }

    cancel() {
      this.run = (this.run || 0) + 1;
      (this.anims || []).forEach((a) => { try { a.cancel(); } catch (e) {} });
      this.anims = [];
      if (!this.box) return;
      this.box.style.height = '';
      this.box.classList.remove('is-switching');
      this.box.querySelectorAll('.fcp__panel').forEach((p) => {
        p.style.opacity = '';
        p.style.transform = '';
        p.querySelectorAll('.ftc__track.is-entering').forEach((t) => t.classList.remove('is-entering'));
        p.querySelectorAll('.ftc__track > *, .product-grid > *').forEach((el) => {
          if (el.matches('ft-reveal:not([played])')) return;
          el.style.opacity = '';
          el.style.transform = '';
        });
      });
    }

    settled() {
      window.lenis?.resize?.();
    }
  }

  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
})();
